import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAppSelector } from '../../hooks'
import { getFormattedDate } from '../../helpers/timeHelper'
import { getGpsTrackerDetails } from '../../modules/GpsTracker'
import { HeaderNavi } from '../navigation'
import Loading from '../common/Loading'
import NotFound from '../../containers/NotFound/NotFound'
import DetailGpsTrackerModal from './DetailGpsTrackerModal'

type Params = {
  device_token: string
}

type GpsTrackerWithGroup = {
  group: IGroup
  tracker: IGpsTracker
}

const flattenGroups = (groups: IGroup[]): IGroup[] => groups.reduce<IGroup[]>(
  (result, group) => [
    ...result,
    group,
    ...flattenGroups(group.child_groups || []),
  ],
  [],
)

const GpsTrackerDetailPage = () => {
  const { device_token: deviceToken } = useParams<Params>()
  const teamGroups = useAppSelector((state) => state.team.groups)
  const today = useMemo(() => getFormattedDate(new Date(), 'YYYY-MM-DD'), [])
  const [group, setGroup] = useState<IGroup | undefined>()
  const [tracker, setTracker] = useState<IGpsTracker | undefined>()
  const [isLoadingInitial, setIsLoadingInitial] = useState(true)

  useEffect(() => {
    let isCancelled = false

    const fetchGpsTracker = async () => {
      if (!deviceToken) {
        setIsLoadingInitial(false)
        return
      }

      try {
        const gpsTrackerCandidates = await Promise.all(
          flattenGroups(teamGroups || []).map(async (candidateGroup) => {
            try {
              const candidateTracker = await getGpsTrackerDetails(
                candidateGroup.id,
                deviceToken,
                { date: today },
              )

              return {
                group: candidateGroup,
                tracker: candidateTracker,
              } as GpsTrackerWithGroup
            } catch {
              return null
            }
          }),
        )

        const gpsTrackerWithGroup = gpsTrackerCandidates.find(
          (candidate): candidate is GpsTrackerWithGroup => candidate !== null,
        )

        if (isCancelled) return

        setGroup(gpsTrackerWithGroup?.group)
        setTracker(gpsTrackerWithGroup?.tracker)
      } catch (error: unknown) {
        if (!isCancelled) {
          console.error('Failed to fetch GPS tracker:', error)
          setGroup(undefined)
          setTracker(undefined)
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingInitial(false)
        }
      }
    }

    fetchGpsTracker()

    return () => {
      isCancelled = true
    }
  }, [deviceToken, teamGroups, today])

  if (isLoadingInitial) {
    return (
      <div className="content">
        <HeaderNavi />
        <Loading show={isLoadingInitial} />
      </div>
    )
  }

  if (!group || !tracker) {
    return (
      <div className="content">
        <HeaderNavi />
        <NotFound />
      </div>
    )
  }

  return (
    <DetailGpsTrackerModal
      modalProps={{ tracker, group }}
      displayMode="page"
      fetchInitialDetails={false}
    />
  )
}

export default GpsTrackerDetailPage
